import { ReactElement, useCallback } from 'react';

import { VisitDiagnoses } from '~libs.shared.visit';

import { historicalVisitTranslationKeys } from '../../translations';
import { dispatchVisitDetailsCopyEvent } from '../../visit/form/VisitFormSynchronization.event';
import { useVisitFormCopyAvailable } from '../../visit/form/VisitFormSynchronization.hooks';

import { useHistoricalVisitCqrsGet } from './HistoricalVisit.hooks';
import { HistoricalVisitContentShowVisitLink } from './HistoricalVisitContentShowVisitLink';
import { HistoricalVisitContentSlot } from './HistoricalVisitContentSlot';
import { HistoricalVisitContentSlotCopyLink } from './HistoricalVisitContentSlotCopyLink';

type HistoricalVisitProps = {
   readonly patientId: number;
   readonly visitId: number;
   readonly targetVisitId: number;
   readonly visitDiagnoses: VisitDiagnoses;
   readonly onGoBackLinkClick: VoidFunction;
};

export const HistoricalVisit = (props: HistoricalVisitProps): JSX.Element | null => {
   const { data: historicalVisit, loading: historicalVisitLoading } = useHistoricalVisitCqrsGet(props.visitId);
   const visitFormCopyAvailable = useVisitFormCopyAvailable();

   const subHeader = useCallback((): ReactElement | undefined => {
      if (!visitFormCopyAvailable || !historicalVisit) {
         return undefined;
      }

      const handleClick = (): void => {
         dispatchVisitDetailsCopyEvent({
            diagnoses: {
               behavior: 'append',
               value: historicalVisit.diagnoses.map((diagnosis) => ({
                  code: diagnosis.icd10Code,
                  name: diagnosis.name,
                  additionalInformation: diagnosis.additionalInformation,
                  main: diagnosis.main,
               })),
            },
            interview: historicalVisit.interview,
            subjectiveAssessment: historicalVisit.physicalExamination,
            recommendations: historicalVisit.recommendations,
         });
      };

      return (
         <>
            <HistoricalVisitContentShowVisitLink
               patientId={props.patientId}
               visitId={props.visitId}
               disabled={!historicalVisit.approved}
            />
            <HistoricalVisitContentSlotCopyLink
               label={historicalVisitTranslationKeys.copyIcons.all}
               onClick={handleClick}
            />
         </>
      );
   }, [historicalVisit, props.patientId, props.visitId, visitFormCopyAvailable]);

   return (
      <HistoricalVisitContentSlot
         historicalVisit={historicalVisit}
         onGoBackLinkClick={props.onGoBackLinkClick}
         isLoading={historicalVisitLoading}
         subHeader={subHeader()}
         targetVisitId={props.targetVisitId}
         patientId={props.patientId}
         visitDiagnoses={props.visitDiagnoses}
      />
   );
};
